import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useMatches,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import archivoFontUrl from "../fonts/archivo-latin-var.woff2?url";
import monoFontUrl from "../fonts/jetbrains-mono-latin-var.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { AuthProvider } from "@/hooks/useAuth";
import { Footer } from "@/components/balasaur/Footer";
import { TopBar } from "@/components/balasaur/TopBar";
import { CookieBanner } from "@/components/balasaur/CookieBanner";
import { AnalyticsManager } from "@/components/balasaur/AnalyticsManager";
import { Toaster } from "@/components/ui/sonner";
import { DinoMark } from "@/components/balasaur/DinoMark";
import { DinoRun } from "@/components/balasaur/DinoRun";
import { SITE_ORIGIN, SITE_NAME, SITE_TAGLINE, DEFAULT_OG_IMAGE, jsonLdScript } from "@/lib/seo";

// The top bar belongs to the shell, not to each page. One route refuses it:
// /watched is a full-viewport rating deck that draws its own header with an
// Exit control, and a second nav row above it would put two ways out on the
// same screen. A route that supplies its own header says so here rather than
// the shell keeping a list of paths. The footer is not affected: it carries
// the TMDB and OMDb attribution, which every page owes.
declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    ownsHeader?: boolean;
  }
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-[5px] border border-border bg-panel p-8 text-center">
        <DinoMark className="mx-auto h-10 w-10 text-primary" />
        <div className="mt-5 font-mono text-[64px] font-semibold tabular-nums leading-none tracking-tighter text-text-bright">
          404
        </div>
        <p className="mt-3 text-[15px] font-black tracking-[-0.02em] text-text-dim">
          Signal lost · route not found
        </p>
        <p className="mt-4 text-[13.5px] leading-relaxed text-text-muted">
          This title isn't in our database. The page you tried doesn't exist or moved.
        </p>
        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-[5px] border border-primary bg-primary px-4 py-2 text-[14px] font-bold tracking-[-0.01em] text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Back to grid
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-[5px] border border-border bg-panel p-8 text-center">
        <DinoMark className="mx-auto h-10 w-10 text-primary" />
        <p className="mt-5 text-[15px] font-black tracking-[-0.02em] text-text-dim">
          Process halted · error caught
        </p>
        <h1 className="mt-2 text-[24px] font-black tracking-[-0.02em] text-text-bright">
          This page didn't load
        </h1>
        <p className="mt-3 text-[13.5px] leading-relaxed text-text-muted">
          Something went wrong on our end. You can try again or head back home.
        </p>
        {/* Raw error.message is intentionally not rendered — it can leak
            DB schema, API paths, or env var names. Full error is logged
            server-side via console.error + reportLovableError above. */}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex cursor-pointer items-center justify-center rounded-[5px] border border-primary bg-primary px-4 py-2 text-[14px] font-bold tracking-[-0.01em] text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-[5px] border border-border-strong bg-background px-4 py-2 text-[14px] font-bold tracking-[-0.01em] text-text-bright transition-colors hover:border-primary hover:text-primary"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Balasaur" },
      {
        name: "description",
        content:
          "Your personal entertainment database. Discover, track, and rate movies and TV all in one place.",
      },
      { name: "author", content: "Balasaur" },
      { property: "og:title", content: "Balasaur" },
      {
        property: "og:description",
        content:
          "Your personal entertainment database. Discover, track, and rate movies and TV all in one place.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Balasaur" },
      {
        name: "twitter:description",
        content:
          "Your personal entertainment database. Discover, track, and rate movies and TV all in one place.",
      },
      { property: "og:image", content: DEFAULT_OG_IMAGE },
      { name: "twitter:image", content: DEFAULT_OG_IMAGE },
      { property: "og:site_name", content: SITE_NAME },
    ],
    scripts: [
      jsonLdScript({
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        description: `${SITE_TAGLINE}. Discover, track, and rate movies and TV.`,
        url: SITE_ORIGIN,
      }),
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      // Brand mark in the tab and on home screens. Served from public/ so the
      // URLs are stable.
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      // Every poster/backdrop loads from TMDB's CDN — warming the connection
      // before the first <img> shaves the LCP handshake.
      { rel: "preconnect", href: "https://image.tmdb.org" },
      // Fonts are self-hosted (see styles.css). Preloading them here means the
      // real face is usually in place for first paint, so there is no swap and
      // no layout shift from it.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: archivoFontUrl,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: monoFontUrl,
        crossOrigin: "anonymous",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {/* Keyboard and screen-reader users skip the nav instead of tabbing
            through it on every page. Visible only when focused. */}
        <a
          href="#main"
          className="sr-only z-50 rounded-[5px] bg-primary px-3 py-2 text-[14px] font-bold tracking-[-0.01em] text-primary-foreground focus:not-sr-only focus:fixed focus:left-3 focus:top-3"
        >
          Skip to content
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const ownsHeader = useMatches({
    select: (matches) => matches.some((m) => m.staticData.ownsHeader === true),
  });

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AnalyticsManager />
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <div className="flex min-h-screen flex-col">
          {!ownsHeader && <TopBar />}
          <div className="flex-1">
            <Outlet />
          </div>
          <Footer />
        </div>
        <CookieBanner />
        <Toaster position="bottom-center" />
        {/* Renders null until someone types rawr. See DinoRun. */}
        <DinoRun />
      </AuthProvider>
    </QueryClientProvider>
  );
}
